import React from "react";
import Box from "@mui/joy/Box";
import CircularProgress from "@mui/joy/CircularProgress";
import IconButton from "@mui/joy/IconButton";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { NotificationService } from "../../../common/notification";
import { formatUtcDateTime } from "../../../common/dateTime";
import { Game } from "../../../models/games";
import { AdminExternalAuditInfo, AdminExternalProviderInfo } from "../../../models/externalMarkets";
import { toNum } from "../../bids/analytics/format";
import { extAuditApi } from "../externalMarketsApi";
import { Pager, resolveGameName } from "../components/shared";

const PageSize = 50;

/** Что означают коды действий журнала */
const ACTION_LABELS: Record<string, string> = {
    "provider.settings": "Настройки поставщика",
    "market.enable": "Связка включена",
    "market.disable": "Связка выключена",
    "market.auto-disable": "Связка выключена системой",
    "market.update": "Допуски и лимиты связки",
    "market.sync": "Ручная синхронизация",
    "profit-rule.create": "Правило прибыли добавлено",
    "profit-rule.update": "Правило прибыли изменено",
    "profit-rule.delete": "Правило прибыли удалено",
    "account.create": "Аккаунт добавлен",
    "account.update": "Аккаунт изменён",
    "account.delete": "Аккаунт удалён",
    "account.check": "Проверка ключа и прокси",
    "account.reset-cooldown": "Снято остывание аккаунта",
    "account.no-reseller-role": "Снято право покупки: нет роли Reseller",
    "account.invalid-project": "Снято право покупки: проект не принят",
    "account.rotate-proxy": "Смена прокси аккаунта",
};

function prettyJson(value?: string): string {
    if (!value) {
        return "—";
    }
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
}

type Props = {
    provider: AdminExternalProviderInfo;
    games: Game[];
};

export function AuditTab(props: Props) {
    const { provider, games } = props;
    const [page, setPage] = React.useState(1);
    const [items, setItems] = React.useState<AdminExternalAuditInfo[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await extAuditApi(provider.key, page, PageSize);
            if (res.result && res.value) {
                setItems(res.value.items ?? []);
                setTotal(toNum(res.value.total));
            } else {
                NotificationService.alertError(res.error ?? "Не удалось загрузить журнал изменений");
            }
        } finally {
            setLoading(false);
        }
    }, [provider.key, page]);

    React.useEffect(() => {
        load().catch(console.error);
    }, [load]);

    return (
        <Stack spacing={1.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography level="body-sm">
                    Все изменения настроек поставщика, включая автоматические выключения. Записей: {total}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="sm" variant="outlined" onClick={() => load()} disabled={loading}>
                    <RefreshIcon />
                </IconButton>
            </Box>
            {loading && items.length === 0 ? (
                <CircularProgress size="sm" />
            ) : items.length === 0 ? (
                <Typography level="body-sm">Изменений не было.</Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "auto" }}>
                    <Table size="sm" hoverRow sx={{ minWidth: 1000, "& td": { verticalAlign: "top" } }}>
                        <thead>
                            <tr>
                                <th style={{ width: 150 }}>Когда</th>
                                <th style={{ width: 120 }}>Кто</th>
                                <th style={{ width: 120 }}>Игра</th>
                                <th style={{ width: 220 }}>Действие</th>
                                <th>Было</th>
                                <th>Стало</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((a) => (
                                <tr key={a.id}>
                                    <td>
                                        <Typography level="body-xs">{formatUtcDateTime(a.created)}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{a.changedBy}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">
                                            {a.gameId ? resolveGameName(games, a.gameId) : "—"}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-sm">{ACTION_LABELS[a.action] ?? a.action}</Typography>
                                        <Typography level="body-xs">{a.action}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs" component="pre" sx={{ m: 0, whiteSpace: "pre-wrap" }}>
                                            {prettyJson(a.oldValue)}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs" component="pre" sx={{ m: 0, whiteSpace: "pre-wrap" }}>
                                            {prettyJson(a.newValue)}
                                        </Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pager page={page} pageCount={Math.max(1, Math.ceil(total / PageSize))} loading={loading} onChange={setPage} />
                </Sheet>
            )}
        </Stack>
    );
}
